import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, PenLine, CheckCircle2, Eraser } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/nda`;
const LANG_LABEL = { fr: "Français", en: "English", de: "Deutsch", es: "Español", it: "Italiano", nl: "Nederlands" };

const UI = {
  fr: { read: "Lisez l'accord, puis signez en bas de page", step1: "Votre adresse e-mail", send: "Recevoir mon code",
        step2: "Code reçu par e-mail", verify: "Valider le code", step3: "Votre identité et votre signature",
        first: "Prénom", last: "Nom", company: "Entreprise", draw: "Dessinez votre signature avec le doigt",
        clear: "Effacer", sign: "Signer l'accord", done: "Accord signé", doneHint: "Un exemplaire PDF vous a été envoyé par e-mail.",
        geo: "Partager ma position (facultatif, renforce la preuve)", confidential: "Document confidentiel" },
  en: { read: "Read the agreement, then sign at the bottom", step1: "Your email address", send: "Send me a code",
        step2: "Code received by email", verify: "Verify code", step3: "Your identity and signature",
        first: "First name", last: "Last name", company: "Company", draw: "Draw your signature with your finger",
        clear: "Clear", sign: "Sign the agreement", done: "Agreement signed", doneHint: "A PDF copy has been emailed to you.",
        geo: "Share my location (optional, strengthens evidence)", confidential: "Confidential document" },
};
const ui = (l) => UI[l] || UI.en;

export default function NdaPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const lang = params.get("lang") || "fr";
  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: "", code: "", first_name: "", last_name: "", company: "" });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(null);
  const [geo, setGeo] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const T = ui(lang);

  useEffect(() => {
    document.title = "EmpCo — NDA";
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    axios.get(`${API}/${token}`, { params: { lang } })
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [token, lang]);

  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0B1F33";
    const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); hasInk.current = true;
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasInk.current = false; };

  const sendCode = async () => {
    setBusy(true);
    try { await axios.post(`${API}/${token}/otp`, { email: form.email }); toast.success(T.step2); setStep(2); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const askGeo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => { setGeo({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }); toast.success("Position enregistrée"); },
      () => toast.message("Position non partagée"), { timeout: 8000 });
  };

  const sign = async () => {
    if (!hasInk.current) { toast.error(T.draw); return; }
    if (!accepted) { toast.error(data.mention); return; }
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/${token}/sign`, {
        ...form, lang, accepted: true, geo, signature: canvasRef.current.toDataURL("image/png"),
      });
      setSigned(res);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (data.error) return <div className="min-h-screen grid place-items-center font-mono text-sm text-muted-foreground" data-testid="nda-not-found">404</div>;

  const d = data.document;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="/brand/logo.webp" alt="EmpCo" className="h-7 w-7 rounded" onError={(e) => { e.target.style.display = "none"; }} />
            <span className="font-serif text-lg">EMPCO</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hidden sm:inline">{T.confidential}</span>
            <select value={lang} onChange={(e) => setParams({ lang: e.target.value })}
              className="h-9 rounded-lg border border-border bg-background text-sm px-2" data-testid="nda-lang-select">
              {(data.langs || ["fr"]).map(l => <option key={l} value={l}>{LANG_LABEL[l] || l}</option>)}
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 space-y-8">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2">{T.read}</p>
          <h1 className="text-3xl sm:text-4xl font-serif tracking-tight" data-testid="nda-title">{d.title}</h1>
          <p className="font-mono text-xs text-muted-foreground mt-2">{d.subtitle}</p>
          {d.lang !== "fr" && <p className="text-xs text-[hsl(29_87%_40%)] mt-2" data-testid="nda-notice">{d.notice}</p>}
        </div>

        <article className="hairline p-6 space-y-5 max-h-[60vh] overflow-y-auto text-sm leading-relaxed" data-testid="nda-body">
          {d.parties.map(([h, b]) => <p key={h}><b>{h}</b> — {b}</p>)}
          {d.preamble.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          {d.articles.map(([h, body]) => (
            <section key={h} className="space-y-2">
              <h2 className="font-mono text-[11px] uppercase tracking-widest text-primary pt-2">{h}</h2>
              {body.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
            </section>
          ))}
          <p className="font-mono text-[11px] text-muted-foreground pt-2">{d.signature.place}</p>
          <p className="text-sm">{d.signature.disclosing}</p>
        </article>

        {signed ? (
          <div className="hairline p-8 text-center space-y-3" data-testid="nda-signed">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[hsl(var(--status-green))]" />
            <p className="font-serif text-xl">{T.done}</p>
            <p className="text-sm text-muted-foreground">{T.doneHint}</p>
            <p className="font-mono text-[10px] text-muted-foreground break-all">SHA-256 · {signed.hash}</p>
          </div>
        ) : (
          <div className="hairline p-6 space-y-5" data-testid="nda-sign-form">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> {step === 1 ? T.step1 : step === 2 ? T.step2 : T.step3}
            </p>

            {step === 1 && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="nom@entreprise.com" className="rounded-lg h-11" data-testid="nda-email" />
                <Button onClick={sendCode} disabled={busy || !form.email} className="rounded-lg h-11 bg-primary text-primary-foreground" data-testid="nda-send-code">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} {T.send}
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Input value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                  inputMode="numeric" placeholder="000000" className="rounded-lg h-11 font-mono tracking-[0.4em] text-center" data-testid="nda-code" />
                <Button onClick={() => (form.code.length === 6 ? setStep(3) : toast.error(T.step2))} className="rounded-lg h-11 bg-primary text-primary-foreground" data-testid="nda-verify-code">
                  {T.verify}
                </Button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  {[["first_name", T.first], ["last_name", T.last], ["company", T.company]].map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5 block">{label}</Label>
                      <Input value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                        className="rounded-lg h-11" data-testid={`nda-${k.replace("_", "-")}`} />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-2">
                    <PenLine className="w-3.5 h-3.5" /> {T.draw}
                  </Label>
                  <canvas ref={canvasRef} width={640} height={220}
                    onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                    onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                    className="w-full h-[160px] rounded-lg border border-border bg-white touch-none"
                    data-testid="nda-canvas" />
                  <button onClick={clear} className="mt-2 font-mono text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="nda-clear">
                    <Eraser className="w-3 h-3" /> {T.clear}
                  </button>
                </div>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="nda-accept" />
                  <span>« {data.mention} »</span>
                </label>
                <button onClick={askGeo} className="font-mono text-[11px] text-primary link-underline" data-testid="nda-geo">
                  {geo ? `✓ ${geo.latitude.toFixed(4)}, ${geo.longitude.toFixed(4)}` : T.geo}
                </button>
                <Button onClick={sign} disabled={busy || !form.first_name || !form.last_name || !accepted}
                  className="rounded-lg h-12 w-full bg-primary text-primary-foreground" data-testid="nda-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />} {T.sign}
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
